import * as types from '../mutation-types';
import OpportunitiesAPI from '../../api/opportunities';
import ContactAPI from '../../api/contacts';
import { pendingRequiredQuestions } from '../../helper/opportunityNegotiationFormHelper';

let latestRequestedConversationId;

export const state = {
  opportunity: null,
  uiFlags: {
    isFetching: false,
    isCreatingOpportunity: false,
    isCreatingProduct: false,
    isDeletingProduct: false,
  },
};

export const getters = {
  getOpportunity: _state => _state.opportunity,
  getUIFlags: _state => _state.uiFlags,
};

export const actions = {
  list: async (_, params) => {
    const response = await OpportunitiesAPI.list(params);
    return response.data;
  },
  listByContact: async (_, { contactId, page = 1 }) => {
    const response = await ContactAPI.getOpportunities(contactId, page);
    return response.data;
  },
  board: async (_, params) => {
    const response = await OpportunitiesAPI.board(params);
    return response.data;
  },
  dashboard: async (_, params) => {
    const response = await OpportunitiesAPI.dashboard(params);
    return response.data;
  },
  summary: async () => {
    const response = await OpportunitiesAPI.summary();
    return response.data;
  },
  export: async (_, params) => {
    await OpportunitiesAPI.export(params);
  },
  create: async ({ commit }, payload) => {
    commit(types.default.SET_OPPORTUNITIES_UI_FLAG, {
      isCreatingOpportunity: true,
    });
    try {
      const response = await OpportunitiesAPI.create(payload);
      return response.data;
    } finally {
      commit(types.default.SET_OPPORTUNITIES_UI_FLAG, {
        isCreatingOpportunity: false,
      });
    }
  },
  createProduct: async ({ commit }, { opportunityId, productLabelId }) => {
    commit(types.default.SET_OPPORTUNITIES_UI_FLAG, {
      isCreatingProduct: true,
    });
    try {
      const response = await OpportunitiesAPI.createProduct(opportunityId, {
        product_label_id: productLabelId,
      });
      return response.data;
    } finally {
      commit(types.default.SET_OPPORTUNITIES_UI_FLAG, {
        isCreatingProduct: false,
      });
    }
  },
  deleteProduct: async ({ commit }, { opportunityId, productId }) => {
    commit(types.default.SET_OPPORTUNITIES_UI_FLAG, {
      isDeletingProduct: true,
    });
    try {
      const response = await OpportunitiesAPI.deleteProduct(
        opportunityId,
        productId
      );
      return response.data;
    } finally {
      commit(types.default.SET_OPPORTUNITIES_UI_FLAG, {
        isDeletingProduct: false,
      });
    }
  },
  async fetchByConversation({ commit }, { conversationId }) {
    commit(types.default.SET_OPPORTUNITIES_UI_FLAG, { isFetching: true });
    latestRequestedConversationId = conversationId;

    try {
      if (!conversationId) {
        commit(types.default.SET_OPPORTUNITY, null);
        return;
      }

      const response = await OpportunitiesAPI.list({
        conversation_id: conversationId,
        status: 'open',
      });

      if (latestRequestedConversationId !== conversationId) return;
      commit(types.default.SET_OPPORTUNITY, (response.data || [])[0] || null);
    } catch (error) {
      if (latestRequestedConversationId === conversationId) {
        commit(types.default.SET_OPPORTUNITY, null);
      }
    } finally {
      if (latestRequestedConversationId === conversationId) {
        commit(types.default.SET_OPPORTUNITIES_UI_FLAG, { isFetching: false });
      }
    }
  },
  getById: async (_, opportunityId) => {
    const response = await OpportunitiesAPI.getById(opportunityId);
    return response.data;
  },
  refreshIfCurrent: ({ commit, state: _state }, opportunity) => {
    if (
      _state.opportunity &&
      opportunity &&
      Number(_state.opportunity.id) === Number(opportunity.id)
    ) {
      commit(types.default.SET_OPPORTUNITY, opportunity);
    }
  },
  updateById: async (_, { opportunityId, payload }) => {
    const response = await OpportunitiesAPI.updateById(opportunityId, payload);
    return response.data;
  },
  updateStatus: async (_, { opportunityId, status, lossReasonId = null }) => {
    const response = await OpportunitiesAPI.updateStatus(
      opportunityId,
      status,
      lossReasonId
    );
    return response.data;
  },
  moveStage: async (_, { opportunityId, pipelineStageId }) => {
    const response = await OpportunitiesAPI.moveStage(
      opportunityId,
      pipelineStageId
    );
    return response.data;
  },
  assignOwner: async (_, { opportunityId, ownerId }) => {
    const response = await OpportunitiesAPI.assignOwner(opportunityId, ownerId);
    return response.data;
  },
  updateNegotiationFormAnswers: async (
    { dispatch },
    { opportunityId, answers }
  ) => {
    const response = await OpportunitiesAPI.updateNegotiationFormAnswers(
      opportunityId,
      answers
    );
    dispatch('refreshIfCurrent', response.data);
    return response.data;
  },
  pendingNegotiationQuestionIds: async (
    { dispatch },
    { opportunity, stage }
  ) => {
    const negotiationForm = await dispatch(
      'opportunityPipelines/getNegotiationForm',
      opportunity.pipeline_id,
      { root: true }
    );
    if (!negotiationForm || negotiationForm.status !== 'active') {
      return [];
    }

    return pendingRequiredQuestions(
      negotiationForm.questions || [],
      stage,
      opportunity.negotiation_form_answers || {}
    ).map(question => question.id);
  },
  updateTemperature: async ({ dispatch }, { opportunityId, temperature }) => {
    const response = await OpportunitiesAPI.updateTemperature(
      opportunityId,
      temperature
    );
    dispatch('refreshIfCurrent', response.data);
    return response.data;
  },
  getNotes: async (_, opportunityId) => {
    const response = await OpportunitiesAPI.getNotes(opportunityId);
    return response.data;
  },
  createNote: async (_, { opportunityId, content }) => {
    const response = await OpportunitiesAPI.createNote(opportunityId, content);
    return response.data;
  },
  getHistories: async (_, opportunityId) => {
    const response = await OpportunitiesAPI.getHistories(opportunityId);
    return response.data;
  },
  getActivities: async (_, opportunityId) => {
    const response = await OpportunitiesAPI.getActivities(opportunityId);
    return response.data;
  },
  createActivity: async (_, { opportunityId, payload }) => {
    const response = await OpportunitiesAPI.createActivity(
      opportunityId,
      payload
    );
    return response.data;
  },
  updateActivityStatus: async (_, { opportunityId, activityId, status }) => {
    const response = await OpportunitiesAPI.updateActivityStatus(
      opportunityId,
      activityId,
      status
    );
    return response.data;
  },
  rescheduleActivity: async (_, { opportunityId, activityId, scheduledAt }) => {
    const response = await OpportunitiesAPI.rescheduleActivity(
      opportunityId,
      activityId,
      scheduledAt
    );
    return response.data;
  },
  getProducts: async (_, opportunityId) => {
    const response = await OpportunitiesAPI.getProducts(opportunityId);
    return response.data;
  },
};

export const mutations = {
  [types.default.SET_OPPORTUNITIES_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },
  [types.default.SET_OPPORTUNITY](_state, data) {
    _state.opportunity = data;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
